import Button from '@/components/base/button/button';
import PhosphorIcon from '@/components/base/icon/phosphor-icon';
import {
  NavigationStore,
  useNavigationStore,
} from '@/features/navigation/store/navigation-store';
import type { Navigation } from '@/features/navigation/types/navigation';
import { checkIfDeviceIsTouchScreen } from '@/features/navigation/utils/check-if-device-is-touch-screen';
import { useRouter } from '@/navigation';
import { CaretCircleRight, CaretRight } from '@phosphor-icons/react';
import { clsx } from 'clsx';
import Image from 'next/image';
import menuTip from 'public/images/Menu_tip.svg';
import type { MouseEvent } from 'react';

interface Props {
  item: Navigation;
  onMouseEnter: (id: number, isKeyboard?: boolean) => void;
  onMouseLeave: () => void;
}
const selector = ({ subcategoryId }: NavigationStore) => ({
  subcategoryId,
});

export const DropDownNavigationItem = ({
  item,
  onMouseEnter,
  onMouseLeave,
  ...rest
}: Props) => {
  const { title, href, children, id } = item;
  const { push } = useRouter();
  const { subcategoryId } = useNavigationStore(selector);

  const isTouchScreenDevice = checkIfDeviceIsTouchScreen();

  const isElementActive = subcategoryId === id;

  const onSubcategoryClick = ({ detail }: MouseEvent<HTMLLIElement>) => {
    //detail 0 means that the event was triggered by keyboard
    if (isTouchScreenDevice || detail === 0) {
      onMouseEnter(id, true);
    } else if (href) {
      push(href);
    }
  };

  const hasSubmenuChildren = children && children?.length >= 1;

  return hasSubmenuChildren ? (
    <li
      onMouseEnter={() => onMouseEnter(id)}
      onMouseLeave={onMouseLeave}
      className="relative w-full list-none"
      onClick={onSubcategoryClick}
      aria-owns={`second-level-${id}`}
    >
      <Image
        src={menuTip}
        alt="Tip"
        width={11}
        height={26}
        //35px = Padding from parent(24px) + width of element(11px) = 35px
        className={clsx(
          '-translate-x-5 -translate-y-1/2 pointer-events-none absolute top-1/2 right-[-35px] opacity-0 duration-100',
          isElementActive && 'translate-x-0 opacity-100',
        )}
      />

      <Button
        className="!max-w-full !px-xs h-clickable-minimum w-full"
        variant="tertiary-no-frame"
        data-dropdown-focus-id={id}
        aria-expanded={isElementActive}
        {...rest}
      >
        <div className="flex h-full w-full justify-between gap-xxs">
          <span
            className={clsx(
              'self-center whitespace-nowrap text-nav-label',
              isElementActive ? 'text-brand' : 'text-default',
            )}
          >
            {title}
          </span>
          <PhosphorIcon
            Icon={isElementActive ? CaretCircleRight : CaretRight}
            variant="bold"
            className={clsx(
              'icon-m',
              isElementActive ? 'text-strong' : 'text-default',
            )}
          />
        </div>
      </Button>
    </li>
  ) : (
    <li>
      <Button
        className={clsx('!px-xs flex h-clickable-minimum w-full')}
        variant="tertiary-no-frame"
        isLink
        href={href ?? '#'}
        linkRestProps={rest}
        {...rest}
      >
        <span
          className={clsx(
            'whitespace-nowrap text-nav-label',
            isElementActive ? 'text-strong' : 'text-default',
          )}
        >
          {title}
        </span>
      </Button>
    </li>
  );
};
